// Symptom & pain follow-up engine (SYMPTOM_FOLLOWUP_PLAN.md).
// Pure data + helpers — safe to import from both the API (Lambda) and the web SPA.
//
// Decisions encoded here:
//   D-SF-1   each check-in is a NEW event (not a mutation)
//   D-SF-2   identity key = normalized name + region
//   D-SF-3   severity 0 + resolvedAt is the resolution sentinel
//   D-SF-6   auto-close after N days of no in-app update
//   D-SF-8   configurable lookback window (1–8 weeks)
//   D-SF-9   snooze state stored server-side; while snoozed a group is still
//            returned (with snoozedUntil set) but is never due and never
//            auto-closed — manual check-in views (OPEN_SYMPTOMS_CHECKIN_PLAN.md)
//            can still list it; reminder UIs filter on dueForCheckIn instead
//   D-SF-I1  per-symptom elapsed-time reminder interval REPLACES the slot-based
//            frequency engine (D-SF-5/D-SF-10, see SYMPTOM_REMINDER_INTERVAL_PLAN.md)
//   D-SF-I3  a symptom is due when now − lastUpdate ≥ its resolved interval
//   D-SF-I9  due math is pure elapsed time, timezone-free — do not reintroduce
//            local-day/slot logic here; snooze gates due and auto-close before
//            either is computed (preserve that order)

import type { MedicalEvent } from "./types.js";
import {
  resolveProactiveSettings,
  resolveSymptomReminderIntervalHours,
} from "./proactiveSuggestions.js";
import type { Patient } from "./types.js";
import { resolveSymptomSynonym } from "./symptomClusters.js";
import { painSiteKey } from "./painMap.js";
import type { PainBodyMap } from "./painMap.js";

// ── Constants ─────────────────────────────────────────────────────────────────

const MS_PER_MIN = 60_000;
const MS_PER_HOUR = 60 * MS_PER_MIN;
const MS_PER_DAY = 24 * MS_PER_HOUR;
const MS_PER_WEEK = 7 * MS_PER_DAY;

/** Default lookback for open-item scan (D-SF-8). */
export const DEFAULT_SYMPTOM_FOLLOWUP_LOOKBACK_WEEKS = 2;
export const SYMPTOM_FOLLOWUP_LOOKBACK_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8] as const;
export type SymptomFollowUpLookbackWeeks = (typeof SYMPTOM_FOLLOWUP_LOOKBACK_OPTIONS)[number];

/** Default auto-close threshold (D-SF-6). */
export const DEFAULT_SYMPTOM_AUTO_CLOSE_DAYS = 30;

/** Default snooze duration in hours (D-SF-9). */
export const DEFAULT_SYMPTOM_SNOOZE_HOURS = 2;

/** "Still the same" duplicate-tap throttle window (§5). */
export const SYMPTOM_SAME_THROTTLE_MS = 30 * MS_PER_MIN;

// ── D-SF-2 identity key ───────────────────────────────────────────────────────

/** Normalize a symptom name for identity comparison (D-SF-2). */
export function normSymptomName(name: unknown): string {
  return String(name ?? "").trim().toLowerCase();
}

/**
 * Build the identity key from a symptom name.
 *
 * D-SF-R2 (SYMPTOM_FOLLOWUP_REFINEMENT_PLAN.md): the key is now **region-insensitive** —
 * region/side are display-only — so the same complaint logged once with a region and
 * once without no longer fragments into two cards. For headaches the name is always
 * "headache", which also unifies a head pain logged once as a `headache` event and once
 * as a `symptom` named "headache" into a single group. The `region` parameter is kept
 * for call-site compatibility but ignored.
 *
 * Synonym resolution is applied after normalization so obvious lexical variants
 * (e.g. "tummy ache", "stomachache", "nauseous") map to a single canonical key.
 */
export function symptomFollowUpKey(name: string, _region?: string): string {
  return resolveSymptomSynonym(normSymptomName(name));
}

/**
 * The single place a symptom/headache event becomes a follow-up identity
 * (PAIN_TRACKING_PLAN.md Task 1.3). Consolidates the five near-identical
 * symptom-vs-headache derivation blocks that used to live in
 * symptomFollowUp / symptomPersistence / symptomStats / condition /
 * sideEffectSymptoms.
 *
 * D-PAIN-4 site-awareness: when the payload carries a `bodyMap` with a `siteId`,
 * `key` becomes `symptomFollowUpKey(name) + "|" + siteId` so two differently
 * located pains that share a name no longer merge. When `bodyMap` is absent —
 * which is EVERY event captured before the Pain page shipped — `key` is
 * byte-identical to `symptomFollowUpKey(name)` (H4). `symptomFollowUpKey` itself
 * is unchanged.
 *
 * NOTE: named `FollowUpIdentity`, not `SymptomIdentity` as the plan's schema
 * block shows, because `shared/src/condition.ts` already exports a different
 * `SymptomIdentity` and both flow through `index.ts`'s `export *`.
 */
export interface FollowUpIdentity {
  key: string;
  name: string;
  region?: string;
  side?: string;
  eventType: "symptom" | "headache";
  siteId?: string;
}

export function symptomIdentityOf(e: MedicalEvent): FollowUpIdentity | null {
  const p = e.payload as Record<string, unknown>;
  const bodyMap = (p.bodyMap ?? null) as PainBodyMap | null;
  if (e.eventType === "symptom") {
    const name = typeof p.name === "string" ? p.name.trim() : "";
    if (!name) return null;
    const region = typeof p.region === "string" ? p.region : undefined;
    const side = typeof p.side === "string" ? p.side : undefined;
    return {
      key: painSiteKey(symptomFollowUpKey(name, region), bodyMap),
      name,
      region,
      side,
      eventType: "symptom",
      siteId: bodyMap?.siteId,
    };
  }
  if (e.eventType === "headache") {
    // Headaches are always named "headache" for identity purposes (D-SF-R2).
    const region = "head";
    const side = typeof p.side === "string" ? p.side : undefined;
    return {
      key: painSiteKey(symptomFollowUpKey("headache", region), bodyMap),
      name: "headache",
      region,
      side,
      eventType: "headache",
      siteId: bodyMap?.siteId,
    };
  }
  return null;
}

// ── Wire shape ────────────────────────────────────────────────────────────────

/** A single historical severity data point carried in the card sparkline. */
export interface SymptomHistoryPoint {
  at: string;     // ISO-8601
  severity: number; // 0..10 (0 = resolution)
}

/** Event type of the events in this group. */
export type SymptomFollowUpEventType = "symptom" | "headache";

/**
 * An open symptom/pain being tracked (D-SF-5). Returned from buildOpenSymptoms.
 * Carries enough for the card UI and the snooze filter without re-reading DDB.
 */
export interface OpenSymptom {
  /** D-SF-2 identity key, e.g. "lower back pain|back-lower". */
  key: string;
  /** Display name as the user first reported it (from the earliest event in group). */
  name: string;
  /** Body region (SymptomRegion or headache location bucket), absent when not set. */
  region?: string;
  /** Side, when set on the most recent event. */
  side?: string;
  eventType: SymptomFollowUpEventType;
  /** D-PAIN-4: present once the group has a pinpoint bodyMap (PAIN_TRACKING_PLAN.md).
   *  Callers writing a follow-up for this symptom MUST pass this back as `siteId`
   *  (SymptomFollowUpCard/api recordSymptomFollowUp) — otherwise the resolution
   *  event's identity key silently drops the `|siteId` suffix and forks off a new,
   *  disconnected group, leaving the real site's card stuck open forever. */
  siteId?: string;
  /** Most recent non-resolution severity (1–10). */
  currentSeverity: number;
  /** ISO-8601 of the most recent event in this group. */
  lastUpdatedAt: string;
  /** ISO-8601 of the first report in this group. */
  firstReportedAt: string;
  /** Last ~10 severity points, newest first, for the sparkline. */
  history: SymptomHistoryPoint[];
  /**
   * D-SF-I1/I3: whether this symptom is currently "due for check-in" —
   * now − lastUpdatedAt ≥ reminderIntervalHours.
   */
  dueForCheckIn: boolean;
  /** This symptom's resolved reminder interval in hours (D-SF-I1, default 6). */
  reminderIntervalHours: number;
  /** ISO-8601 instant when this symptom next becomes due: lastUpdatedAt + interval. */
  nextDueAt: string;
  /**
   * D-SF-9: set iff this symptom is snoozed at build time (snoozedUntil is in
   * the future). While set, dueForCheckIn is always false — reminder UIs filter
   * on dueForCheckIn and so never surface snoozed items, but manual check-in
   * views (OPEN_SYMPTOMS_CHECKIN_PLAN.md) still list them.
   */
  snoozedUntil?: string;
}

/**
 * An open symptom that has exceeded the auto-close threshold (D-SF-6).
 * Returned separately so the API can write the resolution event before
 * returning the suggestions list. buildOpenSymptoms stays pure (no I/O).
 */
export interface StaleSymptom {
  key: string;
  name: string;
  region?: string;
  eventType: SymptomFollowUpEventType;
  lastEventId: string;  // used to copy forward stable descriptors for the resolve write
  lastUpdatedAt: string;
  currentSeverity: number;
  /** The payload fields to carry forward into the auto-resolution event. */
  resolvePayloadBase: Record<string, unknown>;
}

/** Return value of buildOpenSymptoms. */
export interface OpenSymptomsResult {
  open: OpenSymptom[];
  autoClose: StaleSymptom[];
}

// ── Main engine ───────────────────────────────────────────────────────────────

function resolveSymptomSettings(patient: Patient) {
  return resolveProactiveSettings(patient.proactiveSuggestions);
}

/**
 * Extract the follow-up key and display name from a symptom or headache event.
 * For headaches the name is normalized to "headache"; region comes from
 * the headache `location` field bucketed to "head".
 */
function extractKey(e: MedicalEvent): { key: string; name: string; region?: string; side?: string; siteId?: string } | null {
  const id = symptomIdentityOf(e);
  if (!id) return null;
  return { key: id.key, name: id.name, region: id.region, side: id.side, siteId: id.siteId };
}

/**
 * Build the list of open symptoms/headaches for a patient at `now`.
 * Pure — caller supplies loaded events; I/O (auto-close writes) stays in the API.
 *
 * Due-ness is pure elapsed time (D-SF-I1/I3/I9): a symptom is due when
 * now − lastUpdatedAt ≥ its resolved reminder interval (default 6h). No
 * timezone or waking-hours input is needed for this.
 *
 * @param symptomEvents  All non-deleted symptom events (any time range ≥ lookback).
 * @param headacheEvents All non-deleted headache events (same range).
 * @param patient        Used to resolve follow-up settings.
 * @param now            Reference time (default new Date()).
 */
export function buildOpenSymptoms(input: {
  symptomEvents: MedicalEvent[];
  headacheEvents: MedicalEvent[];
  patient: Patient;
  now?: Date;
}): OpenSymptomsResult {
  const now = input.now ?? new Date();
  const settings = resolveSymptomSettings(input.patient);

  if (!settings.symptomFollowUpEnabled) return { open: [], autoClose: [] };

  // Effective lookback window
  const lookbackWeeks = Math.min(
    Math.max(1, Math.round(settings.symptomFollowUpLookbackWeeks ?? DEFAULT_SYMPTOM_FOLLOWUP_LOOKBACK_WEEKS)),
    8
  );
  const since = new Date(now.getTime() - lookbackWeeks * MS_PER_WEEK);

  // Auto-close threshold (null = Off)
  const autoCloseDays =
    settings.symptomFollowUpAutoCloseDays === null
      ? null
      : typeof settings.symptomFollowUpAutoCloseDays === "number" && settings.symptomFollowUpAutoCloseDays > 0
      ? settings.symptomFollowUpAutoCloseDays
      : DEFAULT_SYMPTOM_AUTO_CLOSE_DAYS;
  const autoCloseMs = autoCloseDays !== null ? autoCloseDays * MS_PER_DAY : null;

  // Snooze map
  const snoozes: Record<string, string> = (settings.symptomSnoozes as Record<string, string>) ?? {};

  // Group events by D-SF-2 key; filter to lookback window
  interface Group {
    key: string;
    name: string;
    region?: string;
    side?: string;
    siteId?: string;
    eventType: SymptomFollowUpEventType;
    events: MedicalEvent[]; // oldest first after sort
  }
  const groups = new Map<string, Group>();

  const allEvents = [...input.symptomEvents, ...input.headacheEvents]
    .filter((e) => !e.deleted && Date.parse(e.occurredAt) >= since.getTime())
    .sort((a, b) => Date.parse(a.occurredAt) - Date.parse(b.occurredAt)); // oldest first

  for (const e of allEvents) {
    const extracted = extractKey(e);
    if (!extracted) continue;
    const { key, name, region, side, siteId } = extracted;
    const g = groups.get(key);
    if (g) {
      // Events arrive oldest-first, so keep the most-recent descriptors for display
      // and use the most-recent event type when a group mixes symptom/headache (D-SF-R2).
      if (side) g.side = side;
      if (region) g.region = region;
      if (siteId) g.siteId = siteId;
      g.eventType = e.eventType as SymptomFollowUpEventType;
      g.events.push(e);
    } else {
      groups.set(key, {
        key,
        name,
        region,
        side,
        siteId,
        eventType: e.eventType as SymptomFollowUpEventType,
        events: [e],
      });
    }
  }

  const open: OpenSymptom[] = [];
  const autoClose: StaleSymptom[] = [];

  for (const g of groups.values()) {
    if (!g.events.length) continue;

    // Latest event (events are oldest-first, so last = most recent)
    const latest = g.events[g.events.length - 1];
    const latestPayload = latest.payload as Record<string, unknown>;
    const latestSeverity = typeof latestPayload.severity === "number" ? latestPayload.severity : 0;

    // Group is closed if the latest event is a resolution (severity 0 OR resolvedAt)
    if (latestSeverity === 0 || typeof latestPayload.resolvedAt === "string") continue;

    const lastUpdatedAt = latest.occurredAt;
    const lastUpdatedMs = Date.parse(lastUpdatedAt);
    const firstReportedAt = g.events[0].occurredAt;

    // Snooze (D-SF-9): while snoozed, the group is still returned (manual
    // check-in views list it) but is never due and never auto-closed —
    // exactly the windows during which the old early-`continue` skipped both.
    const snoozedUntilRaw = snoozes[g.key];
    const isSnoozed =
      typeof snoozedUntilRaw === "string" && Date.parse(snoozedUntilRaw) > now.getTime();

    // Auto-close check (D-SF-6)
    if (!isSnoozed && autoCloseMs !== null && now.getTime() - lastUpdatedMs >= autoCloseMs) {
      // Build a minimal payload base from the latest event for the resolve write
      const resolvePayloadBase: Record<string, unknown> = {};
      for (const field of ["name", "region", "side", "location", "presumedKind", "character",
                            "frequency", "impactOnFunction", "followUpKey"] as const) {
        if (latestPayload[field] !== undefined) resolvePayloadBase[field] = latestPayload[field];
      }
      autoClose.push({
        key: g.key,
        name: g.name,
        region: g.region,
        eventType: g.eventType,
        lastEventId: latest.eventId,
        lastUpdatedAt,
        currentSeverity: latestSeverity,
        resolvePayloadBase,
      });
      continue;
    }

    // Build history tail (last 10 points, newest first for sparkline)
    const history: SymptomHistoryPoint[] = g.events
      .slice(-10)
      .reverse()
      .map((e) => ({
        at: e.occurredAt,
        severity: typeof (e.payload as Record<string, unknown>).severity === "number"
          ? (e.payload as Record<string, unknown>).severity as number
          : 0,
      }));

    // Due-for-check-in (D-SF-I1/I3): pure elapsed time against this symptom's
    // resolved reminder interval (own override, or the 6h default).
    const reminderIntervalHours = resolveSymptomReminderIntervalHours(
      settings.symptomReminderIntervals,
      g.key
    );
    const intervalMs = reminderIntervalHours * MS_PER_HOUR;
    const due = !isSnoozed && now.getTime() - lastUpdatedMs >= intervalMs;
    const nextDueAt = new Date(lastUpdatedMs + intervalMs).toISOString();

    open.push({
      key: g.key,
      name: g.name,
      region: g.region,
      side: g.side,
      siteId: g.siteId,
      eventType: g.eventType,
      currentSeverity: latestSeverity,
      lastUpdatedAt,
      firstReportedAt,
      history,
      dueForCheckIn: due,
      reminderIntervalHours,
      nextDueAt,
      ...(isSnoozed ? { snoozedUntil: snoozedUntilRaw } : {}),
    });
  }

  // Sort: due-for-check-in first, then by most-recently-updated descending
  open.sort((a, b) => {
    if (a.dueForCheckIn !== b.dueForCheckIn) return a.dueForCheckIn ? -1 : 1;
    return Date.parse(b.lastUpdatedAt) - Date.parse(a.lastUpdatedAt);
  });

  return { open, autoClose };
}

// ── Settings resolver helpers (used by API + settings UI) ─────────────────────

/** Clamp a lookback-weeks value to the valid range, falling back to default. */
export function resolveSymptomLookbackWeeks(v?: unknown): SymptomFollowUpLookbackWeeks {
  if (typeof v === "number" && SYMPTOM_FOLLOWUP_LOOKBACK_OPTIONS.includes(v as SymptomFollowUpLookbackWeeks)) {
    return v as SymptomFollowUpLookbackWeeks;
  }
  return DEFAULT_SYMPTOM_FOLLOWUP_LOOKBACK_WEEKS;
}

// ── Bedrock tool definition (D-SF-4, §4.3) ───────────────────────────────────

/**
 * Bedrock tool: the model calls this to record a follow-up severity reading for
 * an ongoing symptom or headache (D-SF-1 — a NEW event, not a mutation).
 *
 * This is the chat fallback path described in SYMPTOM_FOLLOWUP_PLAN.md §4.3.
 * The handler in capture.ts resolves the target open symptom by D-SF-2 key
 * (or "most-recently-updated open" when name is omitted), copies forward stable
 * descriptors from the latest event, and persists via the same validate→putEvent
 * write used by the one-tap /symptom-follow-up endpoint (§4.2).
 */
export const RECORD_SYMPTOM_UPDATE_TOOL = {
  name: "record_symptom_update",
  description:
    "Record a follow-up severity reading for an ONGOING symptom or headache (one new data point). " +
    "Use this when the user says how an existing symptom is doing — e.g. 'my headache is better, " +
    "now a 4', 'still have lower back pain, about a 6', 'the pain is completely gone'. " +
    "This writes a NEW event; it does NOT invent a new unrelated complaint. " +
    "For a brand-new symptom or headache the user has never mentioned, use record_symptom or " +
    "record_headache instead. " +
    "Provide name + region to target a specific open symptom (resolved via D-SF-2 key); " +
    "omit both to target the most-recently-updated open symptom. " +
    "NEVER guess a severity — ask for 1–10 (or 'completely gone?') if unclear. " +
    "Set resolved=true when the user says the symptom is entirely gone; this writes severity 0 + resolvedAt.",
  input_schema: {
    type: "object",
    properties: {
      name: {
        type: "string",
        description:
          "Symptom name as previously reported, e.g. 'lower back pain', 'headache'. " +
          "Omit to automatically target the most-recently-updated open symptom.",
      },
      region: {
        type: "string",
        description:
          "Body region (e.g. 'back-lower', 'head', 'leg'). Used with name to identify the symptom via D-SF-2 key.",
      },
      severity: {
        type: "number",
        description: "Current severity on a 1–10 scale. Required unless resolved=true.",
      },
      resolved: {
        type: "boolean",
        description:
          "Set true when the user says the symptom is completely gone. " +
          "Writes severity 0 + resolvedAt and closes the tracking card.",
      },
    },
    required: [],
  },
} as const;
